<template>
  <div class="manager-dashboard">
    <!-- Header avec nom de l'école -->
    <dashboard-context-header title="MON ÉCOLE" @context-change="onContextChange" />

    <!-- Cartes principales -->
    <div class="cards-grid">
      <!-- Card 1: Repas du jour -->
      <div class="card" @click="showMealDetails = true">
        <div class="card-header">
          <h3 class="card-title">🍽️ Repas du jour</h3>
          <span class="badge badge-blue">Aujourd'hui</span>
        </div>
        <p class="card-description">Service du midi - 142 élèves attendus</p>
        <button class="card-link">Voir les détails →</button>
      </div>

      <!-- Card 2: Communications -->
      <div class="card" @click="showCommunications = true">
        <div class="card-header">
          <h3 class="card-title">📢 Communications</h3>
          <span class="badge badge-orange" v-if="unreadMessages > 0">{{ unreadMessages }}</span>
        </div>
        <p class="card-description">Absences, spécificités alimentaires et messages</p>
        <button class="card-link">Voir les messages →</button>
      </div>

      <!-- Card 3: Feedback -->
      <div class="card" @click="showFeedbackForm = true">
        <div class="card-header">
          <h3 class="card-title">📝 Feedback quotidien</h3>
          <span class="badge badge-yellow">En attente</span>
        </div>
        <p class="card-description">Donner votre retour sur les plats du jour</p>
        <button class="card-link">Compléter →</button>
      </div>

      <!-- Card 4: Surplus -->
      <div class="card" @click="showSurplusSection = true">
        <div class="card-header">
          <h3 class="card-title">📦 Gestion surplus</h3>
          <span class="badge badge-red" v-if="hasSurplus">1 alerte</span>
        </div>
        <p class="card-description">Déclarer et valoriser les excédents alimentaires</p>
        <button class="card-link">Gérer →</button>
      </div>

      <!-- Card 5: Statistiques -->
      <div class="card" @click="showStats = true">
        <div class="card-header">
          <h3 class="card-title">📊 Statistiques</h3>
          <span class="badge badge-green">Cette semaine</span>
        </div>
        <p class="card-description">Satisfaction et gaspillage de la semaine</p>
        <button class="card-link">Consulter →</button>
      </div>

      <!-- Card 6: Gaspillage -->
      <div class="card" @click="showWasteSection = true">
        <div class="card-header">
          <h3 class="card-title">♻️ Gaspillage</h3>
          <span class="badge badge-green">-12%</span>
        </div>
        <p class="card-description">Visualisation et sensibilisation au gaspillage</p>
        <button class="card-link">Voir →</button>
      </div>
    </div>

    <!-- Modal: Détails repas du jour -->
    <div v-if="showMealDetails" class="modal-overlay" @click="showMealDetails = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showMealDetails = false">✕</button>
        <h2 class="modal-title">🍽️ Repas du jour</h2>
        <p class="modal-subtitle">Lundi 5 janvier - Service du midi</p>

        <div class="detail-section">
          <h3 class="section-title">Effectifs</h3>
          <div class="stats-row">
            <div class="stat-item">
              <div class="stat-value">142</div>
              <div class="stat-label">Prévus</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">138</div>
              <div class="stat-label">Présents</div>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title">Menu</h3>
          <div class="menu-list">
            <div class="menu-item">
              <span class="menu-label">Entrée</span>
              <span class="menu-value">Taboulé</span>
            </div>
            <div class="menu-item">
              <span class="menu-label">Plat principal</span>
              <span class="menu-value">Sauté de porc* sauce pruneaux</span>
            </div>
            <div class="menu-item">
              <span class="menu-label">Accompagnement</span>
              <span class="menu-value">Carottes vichy</span>
            </div>
            <div class="menu-item">
              <span class="menu-label">Produit laitier</span>
              <span class="menu-value">Yaourt nature BIO</span>
            </div>
            <div class="menu-item">
              <span class="menu-label">Dessert</span>
              <span class="menu-value">Compote poire</span>
            </div>
            <div class="menu-item">
              <span class="menu-label">Sans viande</span>
              <span class="menu-value">Colin sauce pruneaux</span>
            </div>
          </div>
        </div>

        <button
          @click="
            showFeedbackForm = true
            showMealDetails = false
          "
          class="btn btn-primary"
        >
          Donner mon feedback
        </button>
      </div>
    </div>

    <!-- Modal: Communications -->
    <div v-if="showCommunications" class="modal-overlay" @click="showCommunications = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showCommunications = false">✕</button>
        <communication-panel />
      </div>
    </div>

    <!-- Modal: Feedback Form -->
    <div v-if="showFeedbackForm" class="modal-overlay" @click="showFeedbackForm = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showFeedbackForm = false">✕</button>
        <feedback-form @close="showFeedbackForm = false" />
      </div>
    </div>

    <!-- Modal: Surplus -->
    <div v-if="showSurplusSection" class="modal-overlay" @click="showSurplusSection = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showSurplusSection = false">✕</button>
        <h2 class="modal-title">📦 Gestion des surplus</h2>

        <div class="detail-section">
          <div class="alert alert-warning">
            <strong>⚠️ Alerte</strong>
            <p>8kg de surplus à valoriser aujourd'hui</p>
          </div>
        </div>

        <button
          @click="
            showSurplusForm = true
            showSurplusSection = false
          "
          class="btn btn-primary"
        >
          Déclarer un surplus
        </button>
        <button class="btn btn-secondary">Voir les associations disponibles</button>
      </div>
    </div>

    <!-- Modal: Surplus Form -->
    <div v-if="showSurplusForm" class="modal-overlay" @click="showSurplusForm = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showSurplusForm = false">✕</button>
        <surplus-form @close="showSurplusForm = false" />
      </div>
    </div>

    <!-- Modal: Statistiques -->
    <div v-if="showStats" class="modal-overlay" @click="showStats = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showStats = false">✕</button>
        <h2 class="modal-title">📊 Statistiques de la semaine</h2>

        <div class="stats-row">
          <div class="stat-card stat-green">
            <div class="stat-icon">😊</div>
            <div class="stat-value">78%</div>
            <div class="stat-label">Satisfaction</div>
          </div>
          <div class="stat-card stat-blue">
            <div class="stat-icon">📉</div>
            <div class="stat-value">-12%</div>
            <div class="stat-label">Gaspillage</div>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title">Top 3 des plats</h3>
          <div class="ranking-list">
            <div class="ranking-item">
              <span class="ranking-number">1</span>
              <span class="ranking-name">Lasagnes</span>
              <span class="ranking-score">92%</span>
            </div>
            <div class="ranking-item">
              <span class="ranking-number">2</span>
              <span class="ranking-name">Couscous</span>
              <span class="ranking-score">87%</span>
            </div>
            <div class="ranking-item">
              <span class="ranking-number">3</span>
              <span class="ranking-name">Gratin</span>
              <span class="ranking-score">85%</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Gaspillage -->
    <div v-if="showWasteSection" class="modal-overlay" @click="showWasteSection = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showWasteSection = false">✕</button>
        <waste-input-panel :canteen-id="canteenId" variant="barquette" />
      </div>
    </div>
  </div>
</template>

<script>
import FeedbackForm from "./FeedbackForm.vue"
import SurplusForm from "./SurplusForm.vue"
import CommunicationPanel from "@/components/CommunicationPanel.vue"
import WasteInputPanel from "@/components/WasteInputPanel.vue"
import DashboardContextHeader from "@/components/DashboardContextHeader.vue"

export default {
  name: "ManagerDashboard2",
  components: {
    FeedbackForm,
    SurplusForm,
    CommunicationPanel,
    WasteInputPanel,
    DashboardContextHeader,
  },
  data() {
    return {
      showMealDetails: false,
      showCommunications: false,
      showFeedbackForm: false,
      showSurplusSection: false,
      showSurplusForm: false,
      showStats: false,
      showWasteSection: false,
      unreadMessages: 2,
      hasSurplus: true,
      canteenId: null,
      selectedDate: new Date().toISOString().split("T")[0],
      selectedService: null,
    }
  },
  methods: {
    onContextChange({ canteenId, selectedDate, selectedService }) {
      this.canteenId = canteenId
      this.selectedDate = selectedDate
      this.selectedService = selectedService
    },
  },
}
</script>

<style scoped>
/* Layout global */
.manager-dashboard {
  max-width: 1201px;
  margin: 0 auto;
  padding: 1.5rem;
  background-color: #f8f9fa;
  min-height: 100vh;
}

/* Header */
.dashboard-header {
  margin-bottom: 2rem;
  text-align: center;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
  letter-spacing: 0.5px;
}

.dashboard-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0;
}

/* Grille de cartes */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}

@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
}

/* Cartes */
.card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e5e7eb;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.card-description {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 1rem 0;
}

.card-link {
  display: inline-flex;
  align-items: center;
  font-size: 0.875rem;
  color: #2563eb;
  font-weight: 500;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: color 0.2s;
}

.card-link:hover {
  color: #1d4ed8;
}

/* Badges */
.badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  white-space: nowrap;
}

.badge-blue {
  background: #dbeafe;
  color: #1e40af;
}

.badge-orange {
  background: #fed7aa;
  color: #9a3412;
}

.badge-yellow {
  background: #fef3c7;
  color: #92400e;
}

.badge-red {
  background: #fecaca;
  color: #991b1b;
}

.badge-green {
  background: #d1fae5;
  color: #065f46;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-large {
  max-width: 700px;
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: #6b7280;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #e5e7eb;
  color: #1a1a1a;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.modal-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1.5rem 0;
}

/* Sections de détails */
.detail-section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}

/* Stats row */
.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
}

.stat-item {
  text-align: center;
  background: #f9fafb;
  padding: 1rem;
  border-radius: 8px;
}

.stat-value {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.875rem;
  color: #6b7280;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  border: 2px solid;
}

.stat-card.stat-green {
  border-color: #86efac;
  background: #f0fdf4;
}

.stat-card.stat-blue {
  border-color: #93c5fd;
  background: #eff6ff;
}

.stat-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
}

/* Menu list */
.menu-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.menu-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.menu-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.menu-value {
  font-size: 0.875rem;
  color: #1a1a1a;
  font-weight: 600;
}

/* Ranking list */
.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.ranking-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.ranking-number {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #2563eb;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
}

.ranking-name {
  flex: 1;
  font-weight: 500;
  color: #1a1a1a;
}

.ranking-score {
  font-weight: 700;
  color: #16a34a;
}

/* Alerts */
.alert {
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid;
}

.alert-warning {
  background: #fef3c7;
  border-color: #f59e0b;
}

.alert strong {
  display: block;
  margin-bottom: 0.25rem;
  color: #92400e;
}

.alert p {
  margin: 0;
  font-size: 0.875rem;
  color: #78350f;
}

/* Buttons */
.btn {
  width: 100%;
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 0.75rem;
}

.btn-primary {
  background: #2563eb;
  color: white;
}

.btn-primary:hover {
  background: #1d4ed8;
}

.btn-secondary {
  background: #f3f4f6;
  color: #1a1a1a;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

/* Responsive */
@media (max-width: 640px) {
  .manager-dashboard {
    padding: 1rem;
  }

  .dashboard-title {
    font-size: 1.5rem;
  }

  .modal-content {
    padding: 1.5rem;
    border-radius: 12px;
  }
}
</style>
